<template>
	<ui-root v-if="loaded" :data="aiSettings" :part="form" ref="frm" :errs="errors" :actions="dialogControls(env)"></ui-root>
</template>

<script setup lang="ts">
import {connectEventBus, injectEnv} from "@fd/Environment.ts";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {useRouter} from "vue-router";
import {onMounted, onUnmounted, reactive, ref} from "vue";
import type {ErrorMap} from "@fd/PartInjectKeys.ts";
import type {AiSettings} from "@t/setup/AiSettings.ts";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition.ts";
import type {WithMeta} from "@t/fd/WithMeta.ts";
import {NetworkResponse} from "@fd/Types.ts";
import UiRoot from "@fd/containers/UiRoot.vue";

const env = injectEnv();
const navStore = useNavigationStore();
const router = useRouter();

const loaded = ref<boolean>(false);

const errors = reactive<ErrorMap>([]);
const aiSettings = ref<AiSettings|null>(null);
const form = ref<InterfaceRoot<FormDefinition>|null>(null);
const frm = ref(null);
const bus = connectEventBus("AiSettingsPage");

function receivedData(data:AiSettings & WithMeta):void {
	if (data.META) {
		form.value = data.META.form;
		delete data.META;
	}
	aiSettings.value = data;
	loaded.value = true;
}

function loadData() {
	let endpoint = "api/ai/settings?meta=true";
	return env.net.fetch(endpoint)
		.then((result) => result.json())
		.then(receivedData);
}

function saveData():Promise<NetworkResponse> {
	return env.net.fetch("api/ai/settings", "PUT", aiSettings.value);
}


bus.onCommand(["save", "saveAndBack"], async (cmd) => {
	errors.length = 0;
	const response = await saveData();
	if (response.ok) {
		if (cmd === "save") {
			const data = await response.json();
			receivedData(data);
			if (frm.value) {
				frm.value.setDirty(false);
			}
		} else {
			// Go back
			router.back();
		}
		return "ok";
	} else {
		const data = await response.json();
		if (Array.isArray(data?.problems)) {
			for (let problem of data.problems) {
				errors.push(problem);
			}
		}
		console.error(response);
		return "fail";
	}
});

navStore.defineMenu((mb) => mb.standardMenu());

onMounted(() => {
	loadData();
	navStore.forward(env.msg("ai.settings"));
});


</script>

<style>

</style>